/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { When } from "react-if";
import { useToggle } from "react-use";
import styled from "styled-components";
import {
  Box,
  BrickLoader,
  Button,
  FlexContainer,
  theme,
} from "@nordcloud/gnui";
import {
  SearchPresetV2,
  SearchSortField,
  SearchSortOrder,
} from "~/generated/graphql";
import { GranularitySelector, LoaderWrap, PeriodSelector } from "~/components";
import { EstatePeriod } from "~/constants";
import { useQueryState } from "~/hooks";
import { isNotEmpty } from "~/tools";
import {
  convertTags,
  filterEmptyValuesInObject,
  mapSearchPresetFilterToFilter,
} from "../helpers";
import { SearchV2Filters } from "../types";
import { EstateActiveFiltersV2 } from "./ActiveFilters";
import {
  EstateChartsQueryState,
  EstateGroupBy,
  GroupingSelector,
  useEstateChartParameters,
} from "./Charts";
import { FiltersSidebar } from "./FiltersSidebar";
import { useAddSearchPreset, useEstateFilters } from "./hooks";
import { useActiveFilters } from "./hooks/useEstateFilters";
import {
  AddSearchPresetModal,
  EstateFiltersToggleSidebar,
} from "./SearchPresets";

type Props = {
  disableDailyView: boolean;
  disabledFilters: boolean;
};

export function EstateFiltersAndChartParams({
  disableDailyView,
  disabledFilters,
}: Props) {
  const [isPresetsSidebarOpen, togglePresetsSidebar] = useToggle(false);
  const [isAddPresetModalOpen, setIsAddPresetModalOpen] = useState(false);
  const addSearchPreset = useAddSearchPreset();

  const { state } = useQueryState<EstateChartsQueryState>();

  const {
    selectedChart,
    range,
    period,
    periodStart,
    periodEnd,
    granularity,
    isApplyEnabled,
    onApply,
    handlePeriodChangeOnClick,
    handleGranularityChange,
    handleGroupByChange,
    clearRange,
    handleClearFilters,
  } = useEstateChartParameters();

  const {
    filters,
    restFilters,
    isSidebarOpen,
    field,
    order,
    query,
    setQueryState,
    handleUpdate,
    clearFilters,
    toggleFilters,
    toggleIsSidebarOpen,
  } = useEstateFilters();

  const {
    activeFilters,
    removeFields,
    clearFilters: clearActiveFilters,
    removeFilterValueV2,
  } = useActiveFilters(restFilters as SearchV2Filters);

  const showFilters = isNotEmpty(activeFilters);

  const handleSaveFilter = async ({ name }: { name: string }) => {
    const formData = {
      name,
      sort: {
        field: field ?? SearchSortField.Cost,
        order: order ?? SearchSortOrder.Dsc,
      },
      query,
      filter: {
        ...restFilters,
        tags: convertTags(restFilters.tags),
        metadata: convertTags(restFilters.metadata),
      },
    };
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    return addSearchPreset(formData);
  };

  const handleApplySearchPreset = (searchPreset: SearchPresetV2) => {
    const { __typename, ...rest } = searchPreset.filter ?? {};
    setQueryState({
      ...filterEmptyValuesInObject(mapSearchPresetFilterToFilter(rest)),
      page: 0,
      ...(searchPreset.query && { query: searchPreset.query }),
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
      field: searchPreset.sort?.field ?? SearchSortField.Cost,
      order: searchPreset.sort?.order ?? SearchSortOrder.Dsc,
      estatePeriod: state.estatePeriod,
      periodStart: state.periodStart,
      periodEnd: state.periodEnd,
      granularity: state.granularity,
      groupBy: state.groupBy,
      groupByTagKey: state.groupByTagKey,
      groupByBusinessContext: state.groupByBusinessContext,
    });
  };

  const isClearButtonHidden = areFiltersDefault(period, selectedChart);

  return (
    <>
      <Box boxStyle="lightGrey" marginBottom={theme.spacing.spacing04}>
        <FlexContainer justifyContent="space-between">
          <FlexContainer alignItems="center" wrap="wrap">
            <PeriodSelector
              period={period}
              range={range}
              clearRange={clearRange}
              disabled={disabledFilters}
              onChange={handlePeriodChangeOnClick}
            />
            <GranularitySelector
              disableDailyView={disableDailyView}
              granularity={granularity}
              disableSelector={disabledFilters}
              onChange={handleGranularityChange}
            />
            <GroupingSelector
              activeChart={selectedChart}
              disabled={disabledFilters}
              handleGroupByChange={handleGroupByChange}
              periodStart={periodStart}
              periodEnd={periodEnd}
            />
            <Button
              size="md"
              severity="high"
              disabled={!isApplyEnabled}
              mr={theme.spacing.spacing02}
              onClick={onApply}
            >
              Apply
            </Button>
            <When condition={!isClearButtonHidden && !isApplyEnabled}>
              <Button
                icon="cancel"
                size="md"
                severity="low"
                disabled={disabledFilters}
                onClick={handleClearFilters}
              />
            </When>
          </FlexContainer>
          <FlexContainer>
            <Button
              severity="low"
              icon="filter"
              disabled={disabledFilters}
              onClick={() => toggleFilters(true)}
            >
              Filters
            </Button>
            <PredefinedFilters
              css={{ marginLeft: "1rem" }}
              severity="low"
              icon="filterMenu"
              disabled={disabledFilters}
              onClick={() => togglePresetsSidebar(true)}
            />
          </FlexContainer>
        </FlexContainer>
        <FiltersSidebar
          isSidebarOpen={isSidebarOpen}
          toggleIsSidebarOpen={toggleIsSidebarOpen}
          filters={filters}
          updateFilters={handleUpdate}
          toggleFilters={toggleFilters}
          clearFilters={clearFilters}
        />
        <EstateFiltersToggleSidebar
          isOpen={isPresetsSidebarOpen}
          onSearchPresetApply={handleApplySearchPreset}
          onClose={() => togglePresetsSidebar(false)}
        />
        <When condition={showFilters}>
          <Row>
            <Col md="12">
              <div
                css={{
                  paddingTop: theme.spacing.spacing03,
                  marginTop: theme.spacing.spacing03,
                  borderTop: `1px solid ${theme.color.border.border01}`,
                }}
              >
                <LoaderWrap
                  loading={disabledFilters}
                  Component={BrickLoader}
                  viewBox="0 0 16 1"
                >
                  <EstateActiveFiltersV2
                    allowSavingFilters
                    filters={activeFilters}
                    onRemoveKey={removeFields}
                    onClear={clearActiveFilters}
                    onSave={() => setIsAddPresetModalOpen(true)}
                    onRemoveValue={(itemName, index) =>
                      removeFilterValueV2(
                        itemName as keyof SearchV2Filters,
                        index
                      )
                    }
                  />
                </LoaderWrap>
              </div>
            </Col>
          </Row>
        </When>
      </Box>
      <AddSearchPresetModal
        isOpen={isAddPresetModalOpen}
        onSubmit={handleSaveFilter}
        onClose={() => setIsAddPresetModalOpen(false)}
      />
    </>
  );
}

const PredefinedFilters = styled(Button)`
  border-top-right-radius: ${theme.radiusDefault};
  border-bottom-right-radius: ${theme.radiusDefault};
`;

function areFiltersDefault(period: EstatePeriod, groupBy: EstateGroupBy) {
  return (
    groupBy === EstateGroupBy.NONE && period === EstatePeriod.DEFAULT_MONTH
  );
}
